import { getGlobalAudio } from "../AudioStream";
import { EncodersConfig } from "./config";
import { NvencRingAudioSender } from "./NvencRingAudioSender";

export class AudioHardwareEncoder {
  private audioReader: ReadableStreamDefaultReader<AudioData> | null = null;

  private worker: Worker | null = null;

  private isRunning = false;

  private audioFrameCount = 0;

  private audioStream: WritableStream<Uint8Array> | null = null;

  // Etapa 2: audio por SHM ring (sin WebTransport). El PCM lo extrae un AudioWorklet en el hilo de
  // audio (o el main-world como fallback) y el blob sender (con Node) lo escribe al ring que kero
  // lee. shmMode true → NO se usa el module worker ni el WT stream.
  private audioSender: NvencRingAudioSender | null = null;

  private shmMode = false;

  constructor() {}

  async init(
    config: EncodersConfig,
    transport: WebTransport | null,
    shm?: { ringName: string; ringCap: number; nativePath: string } | null,
  ): Promise<void> {
    // Audio por SHM (local): ni module worker ni uni-stream de WebTransport para audio.
    if (shm && shm.nativePath) {
      this.shmMode = true;
      this.audioSender = new NvencRingAudioSender();
      await this.audioSender.init(shm.ringName, shm.ringCap, shm.nativePath);
      console.log("🔊 Audio por SHM ring:", shm.ringName);
      return;
    }

    if (!transport) {
      throw new Error(
        "AudioEncoder.init: transport requerido cuando no hay SHM",
      );
    }

    this.audioStream = await transport.createUnidirectionalStream();

    this.worker = new Worker(
      new URL("./audio-encoder.worker.ts", import.meta.url),

      {
        type: "module",
      },
    );

    this.worker.onmessage = (e) => {
      const msg = e.data;

      switch (msg.type) {
        case "initialized":
          break;

        case "audio-first-chunk":
          this.notifyAudioReady();
          break;

        case "stats":
          break;

        case "error":
          break;

        case "encoder-error":
          console.error("🔊 AudioEncoder worker error:", msg.error);

          break;

        case "stopped":
          console.log("🔊 AudioEncoder worker stopped");

          break;
      }
    };

    this.worker.onerror = (e) => {
      console.error("🔊 AudioEncoder worker error:", e);
    };

    // Fase 2 (lip-sync por epoch compartido) DESACTIVADA: el gap real video-arranque↔audio-arranque
    // (variable, hasta ~48s en re-transmits) se codificaba como offset de pts y el flvmux no podía
    // interlevar A/V con esa divergencia → dropeaba el video a ~3fps. Volvemos a pts relativo
    // (87fps, ~3s lip-sync original) hasta rediseñar el A/V-sync de forma compatible con el flvmux.
    const t0WallMs = null;
    void (window as unknown as { __eriolBroadcastT0Ms?: number })
      .__eriolBroadcastT0Ms;

    this.worker.postMessage(
      {
        type: "init",

        config,

        stream: this.audioStream,

        t0WallMs,
      },

      [this.audioStream],
    );

    await new Promise<void>((resolve) => {
      const handler = (e: MessageEvent) => {
        if (e.data.type === "initialized") {
          this.worker?.removeEventListener("message", handler);

          resolve();
        }
      };

      this.worker?.addEventListener("message", handler);

      setTimeout(resolve, 2000);
    });
  }

  async start(track: MediaStreamTrack) {
    this.isRunning = true;

    this.audioFrameCount = 0;

    if (!this.shmMode) {
      this.processAudioTrack(track);

      return;
    }

    // Preferido: el tap AudioWorklet sobre el contexto de la mezcla (hilo de audio → worker del
    // ring, sin pasar por el main thread del OSR). Si no hay contexto o el worklet no carga, el
    // PCM se extrae acá en el main-world.
    const mixContext = getGlobalAudio()?.AudioStreamContext ?? null;

    const tapped =
      mixContext && this.audioSender
        ? await this.audioSender.startFromTrack(track, mixContext, () =>
            this.notifyAudioReady(),
          )
        : false;

    // stop() pudo llegar mientras cargaba el worklet.
    if (!this.isRunning) return;

    if (!tapped) {
      this.processAudioTrackPcm(track);
    }
  }

  // AV-sync (source-hold): el audio empezó a producir → avisar al main para que arranque el epoch
  // del PTS de video AHORA (video y audio 0-based desde el mismo origen → lip-sync ~0).
  private notifyAudioReady() {
    try {
      (
        window as unknown as {
          electronAPI?: {
            send?: (channel: string, ...args: unknown[]) => void;
          };
        }
      ).electronAPI?.send?.("osr-audio-ready");
    } catch {
      /* fuera del OSR (editor) → no hay electronAPI; no-op */
    }
  }

  // Fallback de la Etapa 2: lee el track, extrae PCM (f32 interleaved) en el main-world y lo manda
  // al ring SHM vía el blob sender. Avisa osr-audio-ready en el 1er frame (source-hold A/V-sync),
  // igual que el worker WT.
  private processAudioTrackPcm(track: MediaStreamTrack) {
    // @ts-expect-error - MediaStreamTrackProcessor might not have types
    const processor = new MediaStreamTrackProcessor({ track });

    const reader = processor.readable.getReader();

    this.audioReader = reader;

    let firstTs: number | null = null;

    let firstSent = false;

    const loop = async () => {
      while (this.isRunning) {
        try {
          const { value: audioData, done } = await reader.read();

          if (done || !audioData) {
            console.log("🔊 Audio track ended (PCM/SHM)");

            break;
          }

          try {
            const baseTs = firstTs ?? (firstTs = audioData.timestamp);

            const relativeTs = audioData.timestamp - baseTs;

            // f32 interleaved (copyTo convierte el formato nativo si hace falta). kero: appsrc audio/x-raw F32LE.
            const size = audioData.allocationSize({
              planeIndex: 0,
              format: "f32",
            });

            const pcm = new ArrayBuffer(size);

            audioData.copyTo(new Uint8Array(pcm), {
              planeIndex: 0,
              format: "f32",
            });

            if (!firstSent) {
              firstSent = true;

              this.notifyAudioReady();
            }

            this.audioSender?.writePcm(pcm, relativeTs);

            this.audioFrameCount++;
          } finally {
            audioData.close();
          }
        } catch (error) {
          console.error("🔊 Error processing audio (PCM/SHM):", error);

          break;
        }
      }
    };

    loop();
  }

  private processAudioTrack(track: MediaStreamTrack) {
    // @ts-expect-error - MediaStreamTrackProcessor might not have types

    const processor = new MediaStreamTrackProcessor({ track });

    const reader = processor.readable.getReader();

    this.audioReader = reader;

    const processAudio = async () => {
      while (this.isRunning) {
        try {
          const { value: audioData, done } = await reader.read();

          if (done || !audioData) {
            console.log("🔊 Audio track ended");

            break;
          }

          if (this.worker) {
            this.worker.postMessage({ type: "audio", audioData }, [audioData]);

            this.audioFrameCount++;
          } else {
            console.warn("🔊 Worker not initialized, dropping audio");

            audioData.close();
          }
        } catch (error) {
          console.error("🔊 Error processing audio:", error);

          break;
        }
      }
    };

    processAudio();
  }

  async stop() {
    if (!this.isRunning) return;

    this.isRunning = false;

    if (this.audioReader) {
      try {
        await this.audioReader.cancel();
      } catch (e) {
        console.warn("🔊 Error cancelling audio reader:", e);
      }

      this.audioReader = null;
    }

    if (this.audioSender) {
      this.audioSender.stop();

      this.audioSender = null;
    }

    if (this.worker) {
      this.worker.postMessage({ type: "stop" });

      setTimeout(() => {
        if (this.worker) {
          this.worker.terminate();

          this.worker = null;
        }
      }, 1000);
    }

    console.log("🛑 AudioEncoder stopped");
  }
}
